(() => {
  'use strict';

  document.documentElement.classList.add('selntz-product-js');

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const formatMoney = (cents, currency) => {
    const value = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currency || 'EUR'
      }).format(value);
    } catch (error) {
      return `${value.toFixed(2)} ${currency || ''}`.trim();
    }
  };

  class ProductGallery {
    constructor(element) {
      this.element = element;
      this.panels = Array.from(element.querySelectorAll('[data-media-panel]'));
      this.thumbnails = Array.from(element.querySelectorAll('[data-gallery-thumbnail]'));
      this.counter = element.querySelector('[data-gallery-counter]');
      this.previous = element.querySelector('[data-gallery-previous]');
      this.next = element.querySelector('[data-gallery-next]');
      this.index = Math.max(0, this.panels.findIndex((panel) => panel.classList.contains('is-active')));
      this.pointerStart = null;

      this.bind();
      this.updateCounter();
    }

    bind() {
      this.thumbnails.forEach((thumbnail) => {
        thumbnail.addEventListener('click', () => this.showById(thumbnail.dataset.mediaId, false));
      });

      this.previous?.addEventListener('click', () => this.showIndex(this.index - 1));
      this.next?.addEventListener('click', () => this.showIndex(this.index + 1));

      this.element.querySelectorAll('[data-zoom-toggle]').forEach((button) => {
        button.addEventListener('click', () => {
          const panel = button.closest('[data-media-panel]');
          const zoomTarget = panel?.querySelector('[data-product-zoom]');
          if (!zoomTarget) return;
          const zoomed = zoomTarget.classList.toggle('is-zoomed');
          button.setAttribute('aria-pressed', String(zoomed));
          button.setAttribute('aria-label', zoomed ? 'Reset product image zoom' : 'Zoom product image');
        });
      });

      const stage = this.element.querySelector('.product-gallery__stage');
      stage?.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'touch') return;
        this.pointerStart = { x: event.clientX, y: event.clientY };
      }, { passive: true });
      stage?.addEventListener('pointerup', (event) => {
        if (!this.pointerStart || event.pointerType !== 'touch') return;
        const deltaX = event.clientX - this.pointerStart.x;
        const deltaY = event.clientY - this.pointerStart.y;
        this.pointerStart = null;
        if (Math.abs(deltaX) < 42 || Math.abs(deltaX) < Math.abs(deltaY)) return;
        this.showIndex(this.index + (deltaX < 0 ? 1 : -1));
      }, { passive: true });
    }

    showById(mediaId, focusThumbnail = false) {
      const index = this.panels.findIndex((panel) => String(panel.dataset.mediaId) === String(mediaId));
      if (index >= 0) this.showIndex(index, focusThumbnail);
    }

    showIndex(nextIndex, focusThumbnail = false) {
      if (this.panels.length < 1) return;
      const wrappedIndex = (nextIndex + this.panels.length) % this.panels.length;
      const previousPanel = this.panels[this.index];
      previousPanel?.querySelectorAll('video').forEach((video) => video.pause());
      previousPanel?.querySelector('[data-product-zoom]')?.classList.remove('is-zoomed');
      const previousZoomButton = previousPanel?.querySelector('[data-zoom-toggle]');
      previousZoomButton?.setAttribute('aria-pressed', 'false');
      previousZoomButton?.setAttribute('aria-label', 'Zoom product image');

      this.panels.forEach((panel, index) => {
        const active = index === wrappedIndex;
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
      });

      this.thumbnails.forEach((thumbnail, index) => {
        const active = String(thumbnail.dataset.mediaId) === String(this.panels[wrappedIndex]?.dataset.mediaId);
        thumbnail.classList.toggle('is-active', active);
        thumbnail.setAttribute('aria-current', String(active));
        if (active) {
          thumbnail.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'nearest', inline: 'nearest' });
          if (focusThumbnail) thumbnail.focus({ preventScroll: true });
        }
      });

      this.index = wrappedIndex;
      this.updateCounter();
    }

    updateCounter() {
      if (this.counter) this.counter.textContent = `${this.index + 1} / ${this.panels.length}`;
    }
  }

  class ProductController {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector('form[data-product-form], form[action*="/cart/add"]');
      this.variantInput = root.querySelector('[data-variant-id]');
      this.optionInputs = Array.from(root.querySelectorAll('[data-option-input]'));
      this.addButton = root.querySelector('[data-add-to-cart]');
      this.addLabel = root.querySelector('[data-add-to-cart-label]');
      this.mobileBar = root.querySelector('[data-mobile-purchase-bar]');
      this.mobileButton = root.querySelector('[data-mobile-add]');
      this.mobileLabel = root.querySelector('[data-mobile-add-label]');
      this.bundleInputs = Array.from(root.querySelectorAll('[data-bundle-input]'));
      this.bundleSelection = root.querySelector('[data-bundle-selection]');
      this.bundlePrices = Array.from(root.querySelectorAll('[data-bundle-price]'));
      this.bundleRegularPrices = Array.from(root.querySelectorAll('[data-bundle-regular-price]'));
      this.dynamicCheckout = root.querySelector('[data-dynamic-checkout]');
      this.bundleCheckoutNote = root.querySelector('[data-bundle-checkout-note]');
      this.currency = root.dataset.currency || 'EUR';
      this.pendingAdd = false;
      this.pendingTimer = null;

      if (this.variantInput) this.variantInput.disabled = false;

      try {
        this.variants = JSON.parse(root.querySelector('[data-product-variants]')?.textContent || '[]');
      } catch (error) {
        this.variants = [];
      }

      const galleryElement = root.querySelector('[data-product-gallery]');
      this.gallery = galleryElement ? new ProductGallery(galleryElement) : null;

      this.bind();
      this.updateAvailability();
      this.updateBundle();
      this.setupStickyPurchaseBar();
    }

    bind() {
      this.optionInputs.forEach((input) => {
        input.addEventListener('change', () => this.onOptionsChanged());
      });

      this.root.querySelector('[data-quantity-minus]')?.addEventListener('click', () => this.adjustQuantity(-1));
      this.root.querySelector('[data-quantity-plus]')?.addEventListener('click', () => this.adjustQuantity(1));
      this.bundleInputs.forEach((input) => {
        input.addEventListener('change', () => this.updateBundle());
      });

      this.mobileButton?.addEventListener('click', () => {
        if (!this.form || !this.addButton || this.addButton.disabled) return;
        if (typeof this.form.requestSubmit === 'function') this.form.requestSubmit(this.addButton);
        else this.addButton.click();
      });

      this.form?.addEventListener('submit', () => {
        if (!this.addButton || this.addButton.disabled) return;
        this.clearMessage();
        this.pendingAdd = true;
        this.setLoading(true);
        window.clearTimeout(this.pendingTimer);
        this.pendingTimer = window.setTimeout(() => {
          this.pendingAdd = false;
          this.setLoading(false);
        }, 10000);
      });

      document.addEventListener('selntz:cart-updated', () => {
        if (!this.pendingAdd) return;
        this.pendingAdd = false;
        window.clearTimeout(this.pendingTimer);
        this.setLoading(false);
        this.showAddedState();
      });

      document.addEventListener('selntz:cart-error', (event) => {
        if (!this.pendingAdd) return;
        this.pendingAdd = false;
        window.clearTimeout(this.pendingTimer);
        this.setLoading(false);
        this.showMessage(event.detail?.message || 'This item could not be added. Please try again.');
      });
    }

    getSelectedOptions() {
      const selected = [];
      this.optionInputs.filter((input) => input.checked).forEach((input) => {
        selected[Number(input.dataset.optionIndex)] = input.value;
      });
      return selected;
    }

    getSelectedVariant() {
      if (!this.optionInputs.length) {
        return this.variants.find((variant) => String(variant.id) === String(this.variantInput?.value)) || this.variants[0];
      }
      const selected = this.getSelectedOptions();
      return this.variants.find((variant) => variant.options.every((option, index) => option === selected[index]));
    }

    onOptionsChanged() {
      this.clearMessage();
      const variant = this.getSelectedVariant();
      this.updateAvailability();
      this.updateSelectedLabels();
      this.updateVariant(variant);
    }

    updateSelectedLabels() {
      this.root.querySelectorAll('[data-option-position]').forEach((fieldset) => {
        const checked = fieldset.querySelector('[data-option-input]:checked');
        const label = fieldset.querySelector('[data-selected-option]');
        if (checked && label) label.textContent = checked.value;
      });
    }

    updateAvailability() {
      if (!this.optionInputs.length || !this.variants.length) return;
      const selected = this.getSelectedOptions();

      this.optionInputs.forEach((input) => {
        const optionIndex = Number(input.dataset.optionIndex);
        const available = this.variants.some((variant) => {
          if (!variant.available || variant.options[optionIndex] !== input.value) return false;
          return variant.options.slice(0, optionIndex).every((option, index) => !selected[index] || option === selected[index]);
        });
        input.disabled = !available && !input.checked;
        input.nextElementSibling?.classList.toggle('is-unavailable', !available);
        input.nextElementSibling?.setAttribute('aria-disabled', String(!available));
      });
    }

    updateVariant(variant) {
      const available = Boolean(variant?.available);
      if (this.variantInput) this.variantInput.value = variant?.id || '';

      this.updatePrice(variant);
      this.updateBundlePrices(variant);
      this.updateButtons(available, variant ? 'Sold out' : 'Unavailable');

      const featuredMediaId = variant?.featured_media?.id || variant?.featured_image?.id;
      if (featuredMediaId) this.gallery?.showById(featuredMediaId);

      if (variant?.id) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }

      this.root.dispatchEvent(new CustomEvent('selntz:variant-changed', {
        bubbles: true,
        detail: { variant }
      }));
    }

    updatePrice(variant) {
      const price = this.root.querySelector('[data-product-price]');
      const compare = this.root.querySelector('[data-compare-price]');
      const saleBadge = this.root.querySelector('[data-sale-badge]');

      if (!variant) {
        if (price) price.textContent = 'Unavailable';
        compare?.classList.add('is-hidden');
        saleBadge?.classList.add('is-hidden');
        return;
      }

      const formattedPrice = formatMoney(variant.price, this.currency);
      if (price) price.textContent = formattedPrice;

      const onSale = Number(variant.compare_at_price) > Number(variant.price);
      if (compare) {
        compare.textContent = onSale ? formatMoney(variant.compare_at_price, this.currency) : '';
        compare.classList.toggle('is-hidden', !onSale);
      }
      if (saleBadge) {
        const percentage = onSale ? Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100) : 0;
        saleBadge.textContent = percentage ? `Save ${percentage}%` : '';
        saleBadge.classList.toggle('is-hidden', !onSale);
      }
    }

    updateButtons(available, unavailableLabel = 'Sold out') {
      const quantity = this.getPurchaseQuantity();
      const availableLabel = quantity > 1 ? `Add ${quantity} to cart` : 'Add to cart';
      if (this.addButton) this.addButton.disabled = !available;
      if (this.mobileButton) this.mobileButton.disabled = !available;
      if (this.addLabel) this.addLabel.textContent = available ? availableLabel : unavailableLabel;
      if (this.mobileLabel) this.mobileLabel.textContent = available ? availableLabel : unavailableLabel;
    }

    getPurchaseQuantity() {
      const selectedBundle = this.bundleInputs.find((input) => input.checked);
      if (selectedBundle) return Math.max(1, Number(selectedBundle.value) || 1);
      const quantityInput = this.root.querySelector('[data-quantity-input]');
      return Math.max(1, Number(quantityInput?.value) || 1);
    }

    updateBundlePrices(variant = this.getSelectedVariant()) {
      const unitPrice = Number(variant?.price) || 0;
      this.bundlePrices.forEach((element) => {
        const quantity = Math.max(1, Number(element.dataset.bundleQuantity) || 1);
        const discount = Math.min(100, Math.max(0, Number(element.dataset.bundleDiscount) || 0));
        const regularTotal = unitPrice * quantity;
        const offerTotal = Math.round(regularTotal * (100 - discount) / 100);
        element.textContent = variant ? formatMoney(offerTotal, this.currency) : 'Unavailable';
      });
      this.bundleRegularPrices.forEach((element) => {
        const quantity = Math.max(1, Number(element.dataset.bundleQuantity) || 1);
        element.textContent = variant ? formatMoney(unitPrice * quantity, this.currency) : '';
      });

      const mobilePrice = this.root.querySelector('[data-mobile-price]');
      if (mobilePrice) {
        const quantity = this.getPurchaseQuantity();
        const selectedPrice = this.bundlePrices.find((element) => Number(element.dataset.bundleQuantity) === quantity);
        const discount = Math.min(100, Math.max(0, Number(selectedPrice?.dataset.bundleDiscount) || 0));
        const offerTotal = Math.round(unitPrice * quantity * (100 - discount) / 100);
        mobilePrice.textContent = variant ? formatMoney(offerTotal, this.currency) : 'Unavailable';
      }
    }

    updateBundle() {
      if (!this.bundleInputs.length) return;
      const selected = this.bundleInputs.find((input) => input.checked) || this.bundleInputs[0];
      const quantity = Math.max(1, Number(selected?.value) || 1);
      if (this.bundleSelection) this.bundleSelection.textContent = selected?.dataset.bundleLabel || `${quantity} items`;
      this.dynamicCheckout?.classList.toggle('is-bundle-hidden', quantity > 1);
      if (this.dynamicCheckout) this.dynamicCheckout.hidden = quantity > 1;
      this.bundleCheckoutNote?.classList.toggle('is-visible', quantity > 1);
      this.updateBundlePrices();
      this.updateButtons(Boolean(this.getSelectedVariant()?.available));
      this.root.dispatchEvent(new CustomEvent('selntz:bundle-changed', {
        bubbles: true,
        detail: { quantity }
      }));
    }

    adjustQuantity(delta) {
      const input = this.root.querySelector('[data-quantity-input]');
      if (!input) return;
      const minimum = Number(input.min || 1);
      input.value = Math.max(minimum, Number(input.value || minimum) + delta);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    setLoading(loading) {
      this.addButton?.classList.toggle('is-loading', loading);
      if (this.addButton) this.addButton.setAttribute('aria-busy', String(loading));
      if (this.mobileButton) this.mobileButton.setAttribute('aria-busy', String(loading));
    }

    showAddedState() {
      this.clearMessage();
      const currentVariant = this.getSelectedVariant();
      if (this.addLabel) this.addLabel.textContent = 'Added';
      if (this.mobileLabel) this.mobileLabel.textContent = 'Added';
      window.setTimeout(() => this.updateButtons(Boolean(currentVariant?.available)), 1600);
    }

    showMessage(message) {
      const element = this.root.querySelector('[data-product-form-message]');
      if (!element) return;
      element.textContent = message;
      element.hidden = false;
    }

    clearMessage() {
      const element = this.root.querySelector('[data-product-form-message]');
      if (!element) return;
      element.textContent = '';
      element.hidden = true;
    }

    setupStickyPurchaseBar() {
      if (!this.mobileBar || !this.addButton) return;
      let buttonBottom = 0;

      const measure = () => {
        const rect = this.addButton.getBoundingClientRect();
        buttonBottom = rect.bottom + window.scrollY;
      };
      const update = () => {
        const isMobile = window.matchMedia('(max-width: 749px)').matches;
        const shouldShow = isMobile && window.scrollY > buttonBottom + 8;
        this.mobileBar.classList.toggle('is-visible', shouldShow);
        this.mobileBar.setAttribute('aria-hidden', String(!shouldShow));
        this.mobileBar.inert = !shouldShow;
        document.documentElement.classList.toggle('selntz-mobile-sticky-visible', shouldShow);
      };

      measure();
      update();
      window.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', () => {
        measure();
        update();
      }, { passive: true });
    }
  }

  const initAccordions = (scope = document) => {
    scope.querySelectorAll('[data-accordion]:not([data-initialized])').forEach((accordion) => {
      accordion.dataset.initialized = 'true';
      accordion.querySelectorAll('[data-accordion-trigger]').forEach((button) => {
        const panel = document.getElementById(button.getAttribute('aria-controls'));
        panel?.setAttribute('aria-hidden', 'true');
        if (panel) panel.inert = true;
        button.addEventListener('click', () => {
          const expanded = button.getAttribute('aria-expanded') === 'true';
          button.setAttribute('aria-expanded', String(!expanded));
          panel?.classList.toggle('is-open', !expanded);
          panel?.setAttribute('aria-hidden', String(expanded));
          if (panel) panel.inert = expanded;
        });
      });
    });
  };

  const initReveals = (scope = document) => {
    const elements = Array.from(scope.querySelectorAll('[data-reveal]:not([data-reveal-ready])'));
    elements.forEach((element) => { element.dataset.revealReady = 'true'; });
    if (!elements.length) return;

    if (reduceMotion.matches || !('IntersectionObserver' in window) || window.Shopify?.designMode) {
      elements.forEach((element) => element.classList.add('is-revealed'));
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -6% 0px' });

    elements.forEach((element) => observer.observe(element));
  };

  const initProductNavigation = (scope = document) => {
    scope.querySelectorAll('[data-product-navigation]:not([data-initialized])').forEach((navigation) => {
      navigation.dataset.initialized = 'true';
      const links = Array.from(navigation.querySelectorAll('[data-product-nav-link]'));
      const targets = links.map((link) => document.querySelector(link.getAttribute('href'))).filter(Boolean);
      if (!targets.length || !('IntersectionObserver' in window)) return;

      const activate = (id) => {
        links.forEach((link) => {
          const active = link.getAttribute('href') === `#${id}`;
          link.classList.toggle('is-active', active);
          if (active) link.setAttribute('aria-current', 'location');
          else link.removeAttribute('aria-current');
        });
      };

      const observer = new IntersectionObserver((entries) => {
        const visible = entries.filter((entry) => entry.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) activate(visible[0].target.id);
      }, { threshold: [0, 0.15, 0.5], rootMargin: '-32% 0px -54% 0px' });
      targets.forEach((target) => observer.observe(target));
    });
  };

  const initProductRoots = (scope = document) => {
    scope.querySelectorAll('[data-product-root]:not([data-product-initialized])').forEach((root) => {
      root.dataset.productInitialized = 'true';
      new ProductController(root);
    });
  };

  const initialize = (scope = document) => {
    initProductRoots(scope);
    initAccordions(scope);
    initReveals(scope);
    initProductNavigation(scope);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize());
  else initialize();

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
